import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export default function History() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/games", { params: { mine: true, limit: 50 } });
        setGames(data);
      } catch (_) {}
    })();
  }, []);

  return (
    <AppLayout>
      <h1 className="font-display font-extrabold text-4xl tracking-tight mb-6">{t("history.title")}</h1>
      <Card className="border-border">
        <CardContent className="p-0">
          {games.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">{t("history.empty")}</div>
          ) : (
            <div className="divide-y divide-border">
              {games.map((g) => {
                const you = g.players.find((p) => p.user_id === user.user_id);
                const opp = g.players.find((p) => p.user_id !== user.user_id);
                const status = g.status;
                let resultLabel = "—", variant = "secondary";
                if (status === "abandoned") { resultLabel = t("history.abandoned"); variant = "outline"; }
                else if (status === "finished") {
                  if (g.winner_id === user.user_id) { resultLabel = t("history.won"); variant = "default"; }
                  else if (g.winner_id) { resultLabel = t("history.lost"); variant = "destructive"; }
                  else { resultLabel = t("history.draw"); variant = "secondary"; }
                } else if (status === "active" || status === "waiting") {
                  resultLabel = status;
                  variant = "secondary";
                }
                return (
                  <button key={g.game_id}
                    onClick={() => navigate(`/game/${g.game_id}`)}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-muted/40 text-left"
                    data-testid={`history-row-${g.game_id}`}>
                    <div className="text-xs text-muted-foreground w-28 tabular-nums">
                      {new Date(g.updated_at || g.created_at).toLocaleString()}
                    </div>
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={opp?.picture} />
                      <AvatarFallback>{(opp?.name || "—").slice(0,2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{opp?.name || "En attente"}</div>
                      <div className="text-xs text-muted-foreground">{g.language.toUpperCase()} · {status}</div>
                    </div>
                    <div className="font-display font-bold tabular-nums text-lg">
                      {you?.score ?? 0} <span className="text-muted-foreground text-sm">— {opp?.score ?? 0}</span>
                    </div>
                    <Badge variant={variant} className="capitalize">{resultLabel}</Badge>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </AppLayout>
  );
}
